import React, { useState } from "react";
import DashboardContent from "../components/DashboardContent";
import { api } from "../apis/configs/axiosConfigs";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";

const APITest = () => {
  const [httpMethod, setHttpMethod] = useState("GET");
  const [endpoint, setEndpoint] = useState("");
  const [payload, setPayload] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const httpMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];

  const methodSupportsPayload = method =>
    method === "POST" || method === "PUT" || method === "PATCH";

  const isValidJson = jsonString => {
    if (!jsonString.trim()) return true; // Empty string is valid
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const formatJson = jsonString => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  const handleSendRequest = async () => {
    if (!endpoint.trim()) {
      setError("Please enter an endpoint URL");
      return;
    }

    if (methodSupportsPayload(httpMethod) && !isValidJson(payload)) {
      setError("Invalid JSON payload");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      const config = {
        method: httpMethod.toLowerCase(),
        url: endpoint,
        timeout: 30000, // 30 second timeout
      };

      if (methodSupportsPayload(httpMethod) && payload.trim()) {
        config.data = JSON.parse(payload);
      }

      const startTime = Date.now();
      const result = await api.request(config);
      const endTime = Date.now();

      setResponse({
        status: result.status,
        statusText: result.statusText,
        headers: result.headers,
        data: result.data,
        responseTime: endTime - startTime,
      });
    } catch (err) {
      if (isProduction) {
        Sentry.captureException(err);
      }

      if (err.response) {
        // Server responded with error status
        setResponse({
          status: err.response.status,
          statusText: err.response.statusText,
          headers: err.response.headers,
          data: err.response.data,
          error: true,
        });
      } else if (err.request) {
        // Request was made but no response received
        setError(
          "No response received from server. Please check the URL and try again."
        );
      } else {
        // Something else happened
        setError(`Request failed: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayloadFormat = () => {
    if (payload.trim()) {
      setPayload(formatJson(payload));
    }
  };

  const PageContent = () => (
    <div className="api-test-container">
      <div className="api-test-info">
        <p>
          <strong>Note:</strong> Enter relative paths (e.g.,
          &quot;/studies&quot;, &quot;/login&quot;) as the API base URL is
          automatically configured. The tool uses the same authentication and
          configuration as other API calls in the application.
        </p>
      </div>
      <div className="api-test-form">
        <div className="api-test-form__row">
          <div className="api-test-form__field">
            <label htmlFor="httpMethod">HTTP Method</label>
            <select
              id="httpMethod"
              value={httpMethod}
              onChange={e => setHttpMethod(e.target.value)}
              className="api-test-form__select">
              {httpMethods.map(method => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>

          <div className="api-test-form__field api-test-form__field--full">
            <label htmlFor="endpoint">Endpoint URL</label>
            <input
              id="endpoint"
              type="text"
              value={endpoint}
              onChange={e => setEndpoint(e.target.value)}
              placeholder="/studies or /login (relative to API base URL)"
              className="api-test-form__input"
            />
          </div>

          <button
            onClick={handleSendRequest}
            disabled={isLoading}
            className="api-test-form__send-button">
            {isLoading ? "Sending..." : "Send Request"}
          </button>
        </div>

        {methodSupportsPayload(httpMethod) && (
          <div className="api-test-form__field">
            <div className="api-test-form__field-header">
              <label htmlFor="payload">JSON Payload</label>
              <button
                type="button"
                onClick={handlePayloadFormat}
                className="api-test-form__format-button">
                Format JSON
              </button>
            </div>
            <textarea
              id="payload"
              value={payload}
              onChange={e => setPayload(e.target.value)}
              placeholder='{"key": "value"}'
              className="api-test-form__textarea"
              rows={8}
            />
          </div>
        )}

        {error && (
          <div className="api-test-error">
            <h4>Error</h4>
            <p>{error}</p>
          </div>
        )}

        {response && (
          <div className="api-test-response">
            <div className="api-test-response__header">
              <h4>
                Response
                {response.error && (
                  <span className="api-test-response__error"> (Error)</span>
                )}
              </h4>
              <div className="api-test-response__status">
                <span
                  className={`api-test-response__status-code ${response.error ? "error" : "success"}`}>
                  {response.status} {response.statusText}
                </span>
                {response.responseTime && (
                  <span className="api-test-response__time">
                    ({response.responseTime}ms)
                  </span>
                )}
              </div>
            </div>

            <div className="api-test-response__section">
              <h5>Headers</h5>
              <pre className="api-test-response__pre">
                {JSON.stringify(response.headers, null, 2)}
              </pre>
            </div>

            <div className="api-test-response__section">
              <h5>Body</h5>
              <pre className="api-test-response__pre">
                {typeof response.data === "object"
                  ? JSON.stringify(response.data, null, 2)
                  : response.data}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={false}
      headerTitle={"Test Backend APIs"}
      headerButton={"close"}
      pageContent={PageContent()}
    />
  );
};

export default APITest;
